// src/components/insight/InsightCard.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, ChevronDown } from '../common/icons';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Insight } from '../../types/insight';

interface InsightCardProps {
  insight: Insight;
  onAccept: () => Promise<void>;
  onSnooze: () => void;
  onDismiss: () => Promise<void>;
}

export function InsightCard({ insight, onAccept, onSnooze, onDismiss }: InsightCardProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [acceptClicked, setAcceptClicked] = useState(false);
  const [showSecondary, setShowSecondary] = useState(false);

  const handleAccept = async () => {
    if (acceptClicked) return;
    setAcceptClicked(true);
    await onAccept();
  };

  const handleSelfAdjust = () => {
    navigate(`/profile/ideal-week?insightId=${insight.id}`);
  };

  const cta = (
    <div className={`flex items-center gap-2 ${isMobile ? 'mt-4 w-full justify-end' : 'flex-shrink-0 ml-2'}`}>
      <button
        onClick={() => setShowSecondary(v => !v)}
        className="insight-btn-ghost flex items-center gap-1"
        aria-expanded={showSecondary}
        aria-label="More options"
      >
        <span>Not now</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${showSecondary ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <button
        onClick={handleAccept}
        disabled={acceptClicked}
        className="insight-btn-accept flex items-center gap-1.5"
        aria-label="Accept suggestion"
      >
        {acceptClicked && (
          <Check className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
        )}
        <span>{acceptClicked ? 'Accepted' : 'Accept'}</span>
      </button>
    </div>
  );

  return (
    <article
      className="insight-card p-8 sm:p-9"
      role="region"
      aria-label="Weekly Insight"
    >
      {/* Aurora 光帶：兩層漂移，產生極光感 */}
      <div className="aurora-band animate-aurora-shift" aria-hidden="true" />
      <div className="aurora-band-2 animate-aurora-shift-2" aria-hidden="true" />

      <div className={`relative z-content flex ${isMobile ? 'flex-col' : 'items-center'} gap-3`}>
        <div className={`flex ${isMobile ? 'items-start w-full' : 'items-center flex-1 min-w-0'}`}>
          <div className="insight-icon-container mr-3 flex-shrink-0">
            <Sparkles
              size={isMobile ? 28 : 36}
              className="text-emerald-700 opacity-95"
              strokeWidth={2}
              aria-hidden="true"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-emerald-900 text-base md:text-lg font-semibold leading-snug">
              {insight.driftSummary}
            </p>
            <p className="text-emerald-900/80 text-base mt-2 leading-relaxed">
              {insight.aiSuggestion.displayText}
            </p>
          </div>
        </div>
        {!isMobile && cta}
      </div>

      {isMobile && cta}

      {/* 次要選項展開 */}
      {showSecondary && (
        <div className="relative z-content flex items-center gap-2 pt-3 mt-1 justify-end flex-wrap">
          <button onClick={handleSelfAdjust} className="insight-btn-secondary">
            Adjust myself
          </button>
          <button onClick={onDismiss} className="insight-btn-secondary">
            Keep as-is
          </button>
          <button onClick={onSnooze} className="insight-btn-secondary">
            Remind later
          </button>
        </div>
      )}
    </article>
  );
}
